import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { BRAND } from '../config';

const Stocks = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stocksData, setStocksData] = useState(null);
  const [prizes, setPrizes] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedSession, setSelectedSession] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editStock, setEditStock] = useState({ prizeId: null, prizeLabel: '', maxQuantity: 0, usedQuantity: 0 });

  const [filters, setFilters] = useState({
    city_id: '',
    pdv_id: ''
  });
  const [showGainModeModal, setShowGainModeModal] = useState(false);
  const [selectedSessionForGainMode, setSelectedSessionForGainMode] = useState(null);
  const [newGainMode, setNewGainMode] = useState('');

  const [showAddStockModal, setShowAddStockModal] = useState(false);
  const [selectedSessionForAddStock, setSelectedSessionForAddStock] = useState(null);
  const [selectedPrizeForAddStock, setSelectedPrizeForAddStock] = useState(null);
  const [stockToAdd, setStockToAdd] = useState(0);
  const [addedStockMap, setAddedStockMap] = useState({});

  // Vue journalière
  const [activeTab, setActiveTab] = useState('daily');
  const [dailyData, setDailyData] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [restockPrize, setRestockPrize] = useState(null);
  const [restockQty, setRestockQty] = useState(0);
  const [restockDate, setRestockDate] = useState(new Date().toISOString().split('T')[0]);

  const loadStocks = useCallback(async () => {
    try {
      const params = { brand: BRAND };
      if (filters.city_id) params.city_id = filters.city_id;
      if (filters.pdv_id) params.pdv_id = filters.pdv_id;

      const response = await api.get('/admin/stocks', { params });
      setStocksData(response.data.sessions);
      setPrizes(response.data.prizes);
    } catch (error) {
      console.error('Error loading stocks:', error);
      setMessage({ type: 'danger', text: 'Erreur lors du chargement des stocks' });
    } finally {
      setLoading(false);
    }
  }, [filters, BRAND]);

  const loadCities = async () => {
    try {
      const response = await api.get('/admin/cities');
      setCities(response.data);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const loadPdvs = async () => {
    try {
      const params = { brand: BRAND };
      if (filters.city_id) params.city_id = filters.city_id;
      const response = await api.get('/admin/pdv', { params });
      setPdvs(response.data);
    } catch (error) {
      console.error('Error loading PDVs:', error);
    }
  };

  const loadDailyOverview = async () => {
    setDailyLoading(true);
    try {
      const response = await api.get('/admin/stocks/daily-overview');
      setDailyData(response.data);
    } catch (error) {
      console.error('Error loading daily overview:', error);
      setMessage({ type: 'danger', text: 'Erreur lors du chargement de la vue journalière' });
    } finally {
      setDailyLoading(false);
    }
  };

  const handleRestock = async () => {
    if (!restockPrize || restockQty <= 0) return;
    try {
      await api.post('/admin/stocks/restock', {
        prize_config_id: restockPrize.prize_id,
        session_id: restockPrize.session_id,
        quantity_added: restockQty,
        restock_date: restockDate
      });
      setMessage({ type: 'success', text: `+${restockQty} ${restockPrize.prize_label} ajoutés pour le ${restockDate}` });
      setShowRestockModal(false);
      setRestockQty(0);
      loadDailyOverview();
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur lors du réapprovisionnement' });
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    loadStocks();
    loadCities();
    loadPdvs();
    loadDailyOverview();
  }, [user, navigate, loadStocks]);

  // Reload PDVs when city changes
  useEffect(() => {
    loadPdvs();
  }, [filters.city_id]);

  // Inject print-specific CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body {
          background: white !important;
          color: black !important;
        }
        nav, header, .navbar { display: none !important; }
        .card .form-select,
        .card:has(.form-select),
        .filters { display: none !important; }
        button[onclick*="window.print()"] { display: none !important; }
        th:last-child, td:last-child { display: none !important; }
        a[href="/dashboard"] { display: none !important; }
        .modal { display: none !important; }
        .btn-outline-light[onclick*="GainMode"] { display: none !important; }
        .min-vh-100 {
          min-height: auto !important;
          background: white !important;
        }
        .card {
          border: 1px solid #000 !important;
          background: white !important;
          page-break-inside: avoid;
          box-shadow: none !important;
        }
        .card-header {
          background: white !important;
          border-bottom: 1px solid #000 !important;
        }
        .table, .table-dark {
          background-color: #fff !important;
          color: #000 !important;
        }
        .table thead th {
          background-color: #f0f0f0 !important;
          color: #000 !important;
          border: 1px solid #000 !important;
        }
        .table tbody td {
          color: #000 !important;
          border: 1px solid #ccc !important;
          background-color: #fff !important;
        }
        .table tr {
          background-color: #fff !important;
        }
        .table-hover tbody tr:hover {
          background-color: #fff !important;
        }
        h1, h2, h3, h4, h5, h6, p, span, div, small, td, th {
          color: #000 !important;
        }
        .text-light, .text-white {
          color: #000 !important;
        }
        .badge {
          border: 1px solid #000 !important;
          color: #000 !important;
          background: #fff !important;
        }
        .progress {
          display: none !important;
        }
        .fas, .far, .fab {
          display: none !important;
        }
        .alert { display: none !important; }
        .spinner-border { display: none !important; }
        .card {
          page-break-inside: avoid;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleEditStock = (session, prize) => {
    setSelectedSession(session);
    setEditStock({
      prizeId: prize.prize_type,
      prizeLabel: prize.prize_label,
      maxQuantity: prize.max_quantity,
      usedQuantity: prize.used_quantity
    });
    setShowEditModal(true);
  };

  const handleSaveStock = async () => {
    try {
      // Trouver l'ID du prize_config à partir du prize_type
      const prizeConfigResponse = await api.get('/admin/prizes', { params: { brand: BRAND } });
      const prizeConfig = prizeConfigResponse.data.find(p => p.prize_type === editStock.prizeId);

      if (!prizeConfig) {
        setMessage({ type: 'danger', text: 'Configuration de lot non trouvée' });
        return;
      }

      // Utiliser le nouvel endpoint pour modifier le quota journalier
      await api.put(`/admin/daily-quota/${selectedSession.pdv_id}/${prizeConfig.id}`, {
        max_quantity: editStock.maxQuantity,
        used_quantity: editStock.usedQuantity
      });

      setMessage({ type: 'success', text: 'Stock mis à jour avec succès' });
      setShowEditModal(false);
      loadStocks();
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur lors de la mise à jour' });
    }
  };

  const handleAddStock = (session, prize) => {
    setSelectedSessionForAddStock(session);
    setSelectedPrizeForAddStock(prize);
    setStockToAdd(0);
    setShowAddStockModal(true);
  };

  const handleSaveAddStock = async () => {
    try {
      // Trouver l'ID du prize_config à partir du prize_type
      const prizeConfigResponse = await api.get('/admin/prizes', { params: { brand: BRAND } });
      const prizeConfig = prizeConfigResponse.data.find(p => p.prize_type === selectedPrizeForAddStock.prize_type);

      if (!prizeConfig) {
        setMessage({ type: 'danger', text: 'Configuration de lot non trouvée' });
        return;
      }

      // Calculer le nouveau max_quantity en ajoutant le stock supplémentaire
      const newMaxQuantity = selectedPrizeForAddStock.max_quantity + stockToAdd;

      // Utiliser le nouvel endpoint pour modifier le quota journalier
      await api.put(`/admin/daily-quota/${selectedSessionForAddStock.pdv_id}/${prizeConfig.id}`, {
        max_quantity: newMaxQuantity,
        used_quantity: selectedPrizeForAddStock.used_quantity
      });

      setMessage({ type: 'success', text: `${stockToAdd} lots ajoutés avec succès` });

      // Mettre à jour le mapa de stock ajouté
      const key = `${selectedSessionForAddStock.session_id}-${selectedPrizeForAddStock.prize_type}`;
      setAddedStockMap(prev => ({
        ...prev,
        [key]: (prev[key] || 0) + stockToAdd
      }));

      setShowAddStockModal(false);
      loadStocks();
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur lors de l\'ajout du stock' });
    }
  };

  const handleChangeGainMode = (session) => {
    setSelectedSessionForGainMode(session);
    setNewGainMode(session.gain_mode || 'PROBABILITY');
    setShowGainModeModal(true);
  };

  const handleSaveGainMode = async () => {
    try {
      await api.put(`/admin/sessions/${selectedSessionForGainMode.session_id}`, {
        gain_mode: newGainMode
      });

      setMessage({ type: 'success', text: 'Mode de gain mis à jour avec succès' });
      setShowGainModeModal(false);
      loadStocks();
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur lors de la mise à jour' });
    }
  };

  const getStockBadge = (percentage, status) => {
    if (status === 'empty') {
      return <span className="badge bg-danger">Épuisé</span>;
    } else if (status === 'low') {
      return <span className="badge bg-warning text-dark">Critique</span>;
    } else if (status === 'medium') {
      return <span className="badge bg-info">Moyen</span>;
    }
    return <span className="badge bg-success">OK</span>;
  };

  const getStockProgressBar = (percentage, status) => {
    let color = 'bg-success';
    if (status === 'empty') color = 'bg-danger';
    else if (status === 'low') color = 'bg-warning';
    else if (status === 'medium') color = 'bg-info';

    return (
      <div className="progress" style={{ height: '8px', backgroundColor: 'rgba(255,255,255,0.1)' }}>
        <div
          className={`progress-bar ${color}`}
          role="progressbar"
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    );
  };

  const filteredDailyPdvs = useMemo(() => {
    if (!dailyData?.pdvs) return [];
    let list = dailyData.pdvs;
    if (filters.city_id) {
      const city = cities.find(c => String(c.id) === String(filters.city_id));
      if (city) list = list.filter(p => p.city_name === city.name);
    }
    if (filters.pdv_id) {
      list = list.filter(p => String(p.pdv_id) === String(filters.pdv_id));
    }
    return list;
  }, [dailyData, filters, cities]);

  const totalsByCity = useMemo(() => {
    if (!stocksData || stocksData.length === 0) return [];

    const byCity = new Map();
    for (const session of stocksData) {
      const cityName = session.city_name || 'N/A';
      const current = byCity.get(cityName) || {
        city_name: cityName,
        sessions_count: 0,
        max_quantity: 0,
        used_quantity: 0,
        remaining_quantity: 0
      };

      current.sessions_count += 1;

      for (const stock of session.stocks || []) {
        current.max_quantity += stock.max_quantity || 0;
        current.used_quantity += stock.used_quantity || 0;
        current.remaining_quantity += stock.remaining_quantity || 0;
      }

      byCity.set(cityName, current);
    }

    return Array.from(byCity.values()).sort((a, b) => a.city_name.localeCompare(b.city_name));
  }, [stocksData]);

  const stockByCityPrize = useMemo(() => {
    if (!stocksData || stocksData.length === 0) return { cities: [], prizes: [], grid: {} };

    const prizeMeta = new Map();
    const grid = {};

    for (const session of stocksData) {
      const city = session.city_name || 'N/A';
      if (!grid[city]) grid[city] = {};

      for (const stock of session.stocks || []) {
        if (!prizeMeta.has(stock.prize_type)) {
          prizeMeta.set(stock.prize_type, { label: stock.prize_label, color: stock.color });
        }
        if (!grid[city][stock.prize_type]) {
          grid[city][stock.prize_type] = { max: 0, used: 0, remaining: 0 };
        }
        grid[city][stock.prize_type].max += stock.max_quantity || 0;
        grid[city][stock.prize_type].used += stock.used_quantity || 0;
        grid[city][stock.prize_type].remaining += stock.remaining_quantity || 0;
      }
    }

    const cities = Object.keys(grid).sort((a, b) => a.localeCompare(b));
    const prizes = Array.from(prizeMeta.entries())
      .map(([type, info]) => ({ type, ...info }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return { cities, prizes, grid };
  }, [stocksData]);

  const handleExportExcel = () => {
    const workbook = XLSX.utils.book_new();
    const today = new Date().toISOString().split('T')[0];

    if (totalsByCity.length > 0) {
      const sheet1 = totalsByCity.map(row => ({
        'Ville': row.city_name,
        'Sessions': row.sessions_count,
        'Stock initial': row.max_quantity,
        'Utilisé': row.used_quantity,
        'Restant': row.remaining_quantity,
        'Progression (%)': row.max_quantity > 0 ? Math.round((row.remaining_quantity / row.max_quantity) * 100) : 0
      }));
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(sheet1), 'Totaux par ville');
    }

    if (stockByCityPrize.cities.length > 0 && stockByCityPrize.prizes.length > 0) {
      const sheet2 = stockByCityPrize.cities.map(city => {
        const row = { 'Ville': city };
        let total = 0;
        for (const p of stockByCityPrize.prizes) {
          const cell = stockByCityPrize.grid[city]?.[p.type];
          row[p.label] = cell?.remaining ?? 0;
          total += cell?.remaining ?? 0;
        }
        row['Total'] = total;
        return row;
      });
      const totalRow = { 'Ville': 'TOTAL GENERAL' };
      let grandTotal = 0;
      for (const p of stockByCityPrize.prizes) {
        const sum = stockByCityPrize.cities.reduce((acc, c) => acc + (stockByCityPrize.grid[c]?.[p.type]?.remaining ?? 0), 0);
        totalRow[p.label] = sum;
        grandTotal += sum;
      }
      totalRow['Total'] = grandTotal;
      sheet2.push(totalRow);
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(sheet2), 'Stock par ville et lot');
    }

    if (stocksData && stocksData.length > 0) {
      const sheet3 = [];
      for (const session of stocksData) {
        for (const stock of session.stocks || []) {
          sheet3.push({
            'Ville': session.city_name || '',
            'PDV': session.pdv_name || '',
            'Session': session.session_name || '',
            'Lot': stock.prize_label,
            'Type': stock.prize_type,
            'Stock initial': stock.max_quantity || 0,
            'Utilisé': stock.used_quantity || 0,
            'Restant': stock.remaining_quantity || 0,
            'Statut': stock.status || ''
          });
        }
      }
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(sheet3), 'Détail par session');
    }

    if (dailyData && dailyData.pdvs && dailyData.days) {
      const dayCols = dailyData.days.map((day, i) => `J${i + 1} (${day})`);
      const sheet4 = [];
      for (const pdv of dailyData.pdvs) {
        for (const prize of pdv.prizes || []) {
          const row = {
            'Ville': pdv.city_name || '',
            'PDV': pdv.pdv_name || '',
            'Lot': prize.prize_label,
            'Initial': prize.initial_quantity || 0
          };
          (prize.days || []).forEach((cell, i) => {
            row[dayCols[i] || `J${i + 1}`] = cell.stock_start ?? 0;
          });
          row['Restant'] = prize.remaining_quantity ?? 0;
          sheet4.push(row);
        }
      }
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(sheet4), 'Vue journalière');

      const sheet5 = [];
      for (const pdv of dailyData.pdvs) {
        for (const prize of pdv.prizes || []) {
          for (const cell of prize.days || []) {
            sheet5.push({
              'Ville': pdv.city_name || '',
              'PDV': pdv.pdv_name || '',
              'Lot': prize.prize_label,
              'Date': cell.date,
              'Stock début de journée': cell.stock_start ?? 0,
              'Gagnés ce jour': cell.wins ?? 0,
              'Réappro ce jour': cell.restock ?? 0
            });
          }
        }
      }
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(sheet5), 'Détail journalier');
    }

    XLSX.writeFile(workbook, `stocks_${BRAND}_${today}.xlsx`);
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #3E2723 0%, #5D4037 100%)' }}>
        <div className="spinner-border text-danger" role="status"></div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ background: 'linear-gradient(135deg, #3E2723 0%, #5D4037 100%)' }}>
      {/* Header */}
      <nav className="navbar navbar-expand-lg navbar-dark sticky-top" style={{ background: 'rgba(62, 39, 35, 0.95)', padding: '12px 0' }}>
        <div className="container">
          <a className="navbar-brand fw-bold d-flex align-items-center" href="/dashboard" style={{ fontSize: '20px' }}>
            <div style={{ width: '40px', height: '40px', background: 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '12px' }}>
              <i className="fas fa-boxes text-white" style={{ fontSize: '18px' }}></i>
            </div>
            <span>American Cola Stocks</span>
          </a>
          <div className="d-flex align-items-center">
            <span className="text-white me-3">
              <i className="fas fa-user-shield me-2"></i>{user?.username}
            </span>
            <a href="/dashboard" className="btn btn-outline-light btn-sm">
              <i className="fas fa-arrow-left me-2"></i>Retour
            </a>
          </div>
        </div>
      </nav>

      <div className="container py-4">
        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-3`} role="alert">
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })}></button>
          </div>
        )}

        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h2 className="text-white fw-bold mb-1">
              <i className="fas fa-warehouse me-2" style={{ color: '#bf0a30' }}></i>
              Gestion des Stocks
            </h2>
            <p className="text-light mb-0">{stocksData?.length || 0} session(s) active(s)</p>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-success" onClick={handleExportExcel} style={{ fontWeight: 'bold' }} disabled={(!stocksData || stocksData.length === 0) && (!dailyData || !dailyData.pdvs || dailyData.pdvs.length === 0)}>
              <i className="fas fa-file-excel me-2"></i>Exporter Excel
            </button>
            <button className="btn btn-danger" onClick={() => window.print()} style={{ fontWeight: 'bold' }}>
              <i className="fas fa-print me-2"></i>Imprimer
            </button>
          </div>
        </div>

        {/* Onglets */}
        <div className="mb-4" style={{ borderBottom: '2px solid rgba(191,10,48,0.3)' }}>
          <button
            onClick={() => setActiveTab('daily')}
            style={{
              background: 'none', border: 'none', padding: '10px 20px', cursor: 'pointer', fontWeight: 'bold',
              color: activeTab === 'daily' ? '#bf0a30' : '#aaa',
              borderBottom: activeTab === 'daily' ? '3px solid #bf0a30' : '3px solid transparent',
              fontSize: '14px'
            }}
          >
            <i className="fas fa-calendar-alt me-2"></i>Vue journalière
          </button>
          <button
            onClick={() => setActiveTab('stocks')}
            style={{
              background: 'none', border: 'none', padding: '10px 20px', cursor: 'pointer', fontWeight: 'bold',
              color: activeTab === 'stocks' ? '#bf0a30' : '#aaa',
              borderBottom: activeTab === 'stocks' ? '3px solid #bf0a30' : '3px solid transparent',
              fontSize: '14px'
            }}
          >
            <i className="fas fa-boxes me-2"></i>Stocks globaux
          </button>
        </div>

        {activeTab === 'stocks' && <>

        {/* Filters */}
        <div className="card border-0 shadow mb-4" style={{ borderRadius: '12px', background: 'rgba(62, 39, 35, 0.9)', border: '1px solid rgba(191, 10, 48, 0.3)' }}>
          <div className="card-body p-3">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label text-light small">Ville</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.city_id}
                  onChange={(e) => setFilters({ ...filters, city_id: e.target.value, pdv_id: '' })}
                  style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#fff' }}
                >
                  <option value="">Toutes les villes</option>
                  {cities.map(city => (
                    <option key={city.id} value={city.id}>{city.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label text-light small">Point de Vente</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.pdv_id}
                  onChange={(e) => setFilters({ ...filters, pdv_id: e.target.value })}
                  disabled={!filters.city_id}
                  style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#fff' }}
                >
                  <option value="">{filters.city_id ? 'Sélectionner un PDV' : 'Tous'}</option>
                  {pdvs.map(pdv => (
                    <option key={pdv.id} value={pdv.id}>{pdv.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Totaux par ville */}
        {totalsByCity.length > 0 && (
          <div className="card border-0 shadow mb-4" style={{ borderRadius: '12px', background: 'rgba(62, 39, 35, 0.9)', border: '1px solid rgba(191, 10, 48, 0.3)' }}>
            <div className="card-header p-3" style={{ background: 'rgba(191, 10, 48, 0.1)', borderBottom: '1px solid rgba(191, 10, 48, 0.3)' }}>
              <h5 className="text-white mb-0">
                <i className="fas fa-city me-2" style={{ color: '#bf0a30' }}></i>
                Totaux par ville
              </h5>
            </div>
            <div className="card-body p-3">
              <div className="table-responsive">
                <table className="table table-dark table-hover mb-0">
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(191, 10, 48, 0.3)' }}>
                      <th style={{ color: '#bf0a30' }}>Ville</th>
                      <th className="text-center" style={{ color: '#bf0a30' }}>Sessions</th>
                      <th className="text-center" style={{ color: '#bf0a30' }}>Stock Initial</th>
                      <th className="text-center" style={{ color: '#bf0a30' }}>Utilisé</th>
                      <th className="text-center" style={{ color: '#bf0a30' }}>Restant</th>
                      <th className="text-center" style={{ color: '#bf0a30' }}>Progression</th>
                    </tr>
                  </thead>
                  <tbody>
                    {totalsByCity.map((row) => {
                      const percentage = row.max_quantity > 0 ? (row.remaining_quantity / row.max_quantity) * 100 : 0;
                      const status =
                        row.max_quantity > 0 && percentage === 0 ? 'empty' :
                        percentage < 20 ? 'low' :
                        percentage < 50 ? 'medium' :
                        'ok';

                      return (
                        <tr key={row.city_name}>
                          <td className="fw-semibold">{row.city_name}</td>
                          <td className="text-center">{row.sessions_count}</td>
                          <td className="text-center">{row.max_quantity}</td>
                          <td className="text-center">{row.used_quantity}</td>
                          <td className="text-center fw-bold" style={{ color: status === 'empty' ? '#dc3545' : status === 'low' ? '#ffc107' : '#fff' }}>
                            {row.remaining_quantity}
                          </td>
                          <td className="text-center" style={{ minWidth: '150px' }}>
                            {getStockProgressBar(Math.round(percentage), status)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Stock par ville et par lot */}
        {stockByCityPrize.cities.length > 0 && stockByCityPrize.prizes.length > 0 && (
          <div className="card border-0 shadow mb-4" style={{ borderRadius: '12px', background: 'rgba(62, 39, 35, 0.9)', border: '1px solid rgba(191, 10, 48, 0.3)' }}>
            <div className="card-header p-3" style={{ background: 'rgba(191, 10, 48, 0.1)', borderBottom: '1px solid rgba(191, 10, 48, 0.3)' }}>
              <h5 className="text-white mb-0">
                <i className="fas fa-table me-2" style={{ color: '#bf0a30' }}></i>
                Stock par ville et par lot
              </h5>
              <small className="text-light">Stock restant par ville pour chaque lot</small>
            </div>
            <div className="card-body p-3">
              <div className="table-responsive">
                <table className="table table-dark table-hover mb-0" style={{ minWidth: '600px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(191, 10, 48, 0.3)' }}>
                      <th style={{ color: '#bf0a30', minWidth: '140px', position: 'sticky', left: 0, background: 'rgba(62, 39, 35, 0.95)' }}>Ville</th>
                      {stockByCityPrize.prizes.map(p => (
                        <th key={p.type} className="text-center" style={{ color: '#bf0a30', minWidth: '100px' }}>
                          <div className="d-flex align-items-center justify-content-center gap-1">
                            <span style={{ width: '10px', height: '10px', backgroundColor: p.color, borderRadius: '2px', display: 'inline-block' }}></span>
                            <span style={{ fontSize: '12px' }}>{p.label}</span>
                          </div>
                        </th>
                      ))}
                      <th className="text-center" style={{ color: '#bf0a30', minWidth: '90px' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockByCityPrize.cities.map(city => {
                      let cityTotal = 0;
                      return (
                        <tr key={city}>
                          <td className="fw-semibold" style={{ position: 'sticky', left: 0, background: 'rgba(62, 39, 35, 0.95)' }}>{city}</td>
                          {stockByCityPrize.prizes.map(p => {
                            const cell = stockByCityPrize.grid[city]?.[p.type];
                            const remaining = cell?.remaining ?? 0;
                            const max = cell?.max ?? 0;
                            cityTotal += remaining;
                            const pct = max > 0 ? (remaining / max) * 100 : 0;
                            const color = max === 0 ? '#666' : remaining === 0 ? '#dc3545' : pct < 20 ? '#ffc107' : '#fff';
                            return (
                              <td key={p.type} className="text-center" style={{ color }}>
                                {max === 0 ? '—' : (
                                  <>
                                    <strong>{remaining}</strong>
                                    <small className="d-block text-muted" style={{ fontSize: '10px' }}>/ {max}</small>
                                  </>
                                )}
                              </td>
                            );
                          })}
                          <td className="text-center fw-bold" style={{ color: '#bf0a30' }}>{cityTotal}</td>
                        </tr>
                      );
                    })}
                    <tr style={{ borderTop: '2px solid rgba(191, 10, 48, 0.4)', background: 'rgba(191, 10, 48, 0.05)' }}>
                      <td className="fw-bold" style={{ color: '#bf0a30', position: 'sticky', left: 0, background: 'rgba(62, 39, 35, 0.95)' }}>Total général</td>
                      {stockByCityPrize.prizes.map(p => {
                        const total = stockByCityPrize.cities.reduce((acc, c) => acc + (stockByCityPrize.grid[c]?.[p.type]?.remaining ?? 0), 0);
                        const totalMax = stockByCityPrize.cities.reduce((acc, c) => acc + (stockByCityPrize.grid[c]?.[p.type]?.max ?? 0), 0);
                        return (
                          <td key={p.type} className="text-center fw-bold" style={{ color: '#bf0a30' }}>
                            {total}
                            <small className="d-block text-muted" style={{ fontSize: '10px' }}>/ {totalMax}</small>
                          </td>
                        );
                      })}
                      <td className="text-center fw-bold" style={{ color: '#bf0a30' }}>
                        {stockByCityPrize.cities.reduce((acc, c) => acc + Object.values(stockByCityPrize.grid[c] || {}).reduce((s, x) => s + (x.remaining || 0), 0), 0)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Stocks Display */}
        {!stocksData || stocksData.length === 0 ? (
          <div className="card border-0 shadow text-center p-5" style={{ borderRadius: '12px', background: 'rgba(62, 39, 35, 0.9)', border: '1px solid rgba(191, 10, 48, 0.3)' }}>
            <i className="fas fa-box-open fa-4x mb-3" style={{ opacity: 0.3, color: '#bf0a30' }}></i>
            <p className="text-light mb-0">Aucune session active trouvée</p>
          </div>
        ) : (
          stocksData.map(session => (
            <div key={session.session_id} className="card border-0 shadow mb-4" style={{ borderRadius: '12px', background: 'rgba(62, 39, 35, 0.9)', border: '1px solid rgba(191, 10, 48, 0.3)' }}>
              <div className="card-header p-3" style={{ background: 'rgba(191, 10, 48, 0.1)', borderBottom: '1px solid rgba(191, 10, 48, 0.2)' }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-1 fw-bold" style={{ color: '#bf0a30' }}>
                      <i className="fas fa-store me-2"></i>{session.pdv_name}
                    </h5>
                    <small className="text-light">
                      <i className="fas fa-map-marker-alt me-1"></i>{session.city_name}
                      <span className="mx-2">•</span>
                      <i className="fas fa-clock me-1"></i>
                      {session.session_name}
                      <span className="mx-2">•</span>
                      <span className={`badge ${session.gain_mode === 'FORCED' ? 'bg-danger' : session.gain_mode === 'RANDOM' ? 'bg-info' : 'bg-success'}`}>
                        {session.gain_mode === 'FORCED' ? 'Gagnant' : session.gain_mode === 'RANDOM' ? 'Aléatoire' : 'Probabilité'}
                      </span>
                    </small>
                  </div>
                  <button
                    className="btn btn-sm btn-outline-light"
                    onClick={() => handleChangeGainMode(session)}
                    title="Changer le mode de gain"
                  >
                    <i className="fas fa-cog me-1"></i>Mode de gain
                  </button>
                </div>
              </div>
              <div className="card-body p-3">
                <div className="table-responsive">
                  <table className="table table-dark table-hover mb-0">
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(191, 10, 48, 0.3)' }}>
                        <th style={{ color: '#bf0a30' }}>Lot</th>
                        <th className="text-center" style={{ color: '#bf0a30' }}>Stock Initial</th>
                        <th className="text-center" style={{ color: '#bf0a30' }}>Stock ajouté</th>
                        <th className="text-center" style={{ color: '#bf0a30' }}>Utilisé</th>
                        <th className="text-center" style={{ color: '#bf0a30' }}>Restant</th>
                        <th className="text-center" style={{ color: '#bf0a30' }}>Statut</th>
                        <th className="text-center" style={{ color: '#bf0a30' }}>Progression</th>
                        <th className="text-center" style={{ color: '#bf0a30' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {session.stocks.map(stock => (
                        <tr key={stock.prize_type}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  backgroundColor: stock.color,
                                  borderRadius: '4px',
                                  marginRight: '10px'
                                }}
                              ></div>
                              <div>
                                <div className="fw-semibold">{stock.prize_label}</div>
                                <small className="text-muted">{stock.prize_name}</small>
                              </div>
                            </div>
                          </td>
                          <td className="text-center">{stock.max_quantity}</td>
                          <td className="text-center" style={{ color: '#28a745', fontWeight: 'bold' }}>
                            {addedStockMap[`${session.session_id}-${stock.prize_type}`] || 0}
                          </td>
                          <td className="text-center">{stock.used_quantity}</td>
                          <td className="text-center fw-bold" style={{ color: stock.status === 'empty' ? '#dc3545' : stock.status === 'low' ? '#ffc107' : '#fff' }}>
                            {stock.remaining_quantity}
                          </td>
                          <td className="text-center">{getStockBadge(stock.percentage, stock.status)}</td>
                          <td className="text-center" style={{ minWidth: '150px' }}>
                            {getStockProgressBar(stock.percentage, stock.status)}
                          </td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-1">
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleAddStock(session, stock)}
                                title="Ajouter du stock"
                              >
                                <i className="fas fa-plus"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleEditStock(session, stock)}
                                title="Modifier le stock"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))
        )}

      </> /* fin onglet stocks */}

      {/* ===== VUE JOURNALIÈRE ===== */}
      {activeTab === 'daily' && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="text-white mb-0">
              <i className="fas fa-store me-2" style={{ color: '#bf0a30' }}></i>
              Stocks par Point de Vente
            </h5>
            <button className="btn btn-sm btn-outline-light" onClick={loadDailyOverview}>
              <i className="fas fa-sync-alt me-1"></i>Actualiser
            </button>
          </div>

          {/* Filtres vue journalière */}
          <div className="card border-0 shadow mb-3" style={{ borderRadius: '12px', background: 'rgba(62, 39, 35, 0.9)', border: '1px solid rgba(191, 10, 48, 0.3)' }}>
            <div className="card-body p-3">
              <div className="row g-3 align-items-end">
                <div className="col-md-4">
                  <label className="form-label text-light small mb-1">
                    <i className="fas fa-city me-1" style={{ color: '#bf0a30' }}></i>Ville
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.city_id}
                    onChange={(e) => setFilters({ ...filters, city_id: e.target.value, pdv_id: '' })}
                    style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#fff' }}
                  >
                    <option value="">Toutes les villes</option>
                    {cities.map(city => (
                      <option key={city.id} value={city.id}>{city.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label text-light small mb-1">
                    <i className="fas fa-store me-1" style={{ color: '#bf0a30' }}></i>Point de Vente
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.pdv_id}
                    onChange={(e) => setFilters({ ...filters, pdv_id: e.target.value })}
                    disabled={!filters.city_id}
                    style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#fff' }}
                  >
                    <option value="">{filters.city_id ? 'Tous les PDVs' : 'Sélectionner une ville d\'abord'}</option>
                    {pdvs.map(pdv => (
                      <option key={pdv.id} value={pdv.id}>{pdv.name}</option>
                    ))}
                  </select>
                </div>
                {(filters.city_id || filters.pdv_id) && (
                  <div className="col-md-2">
                    <button
                      className="btn btn-sm btn-outline-light w-100"
                      onClick={() => setFilters({ city_id: '', pdv_id: '' })}
                    >
                      <i className="fas fa-times me-1"></i>Réinitialiser
                    </button>
                  </div>
                )}
                <div className="col-md-2 text-end">
                  <small className="text-light">
                    <strong className="text-white">{filteredDailyPdvs.length}</strong> PDV{filteredDailyPdvs.length > 1 ? 's' : ''}
                  </small>
                </div>
              </div>
            </div>
          </div>

          {dailyLoading ? (
            <div className="text-center py-5"><div className="spinner-border text-danger" role="status"></div></div>
          ) : !dailyData || filteredDailyPdvs.length === 0 ? (
            <div className="text-center text-light py-5">
              {dailyData && dailyData.pdvs?.length > 0
                ? 'Aucun point de vente ne correspond aux filtres'
                : 'Aucune donnée disponible'}
            </div>
          ) : (
            <div>
              {filteredDailyPdvs.map(pdv => {
                const hasAlert = pdv.prizes.some(p => p.remaining_quantity <= 5 && p.remaining_quantity > 0) || pdv.prizes.some(p => p.remaining_quantity === 0 && p.initial_quantity > 0);
                return (
                  <div key={pdv.pdv_id} className="card border-0 shadow mb-4"
                    style={{ borderRadius: '12px', background: 'rgba(62,39,35,0.9)', border: `1px solid ${hasAlert ? 'rgba(255,165,0,0.5)' : 'rgba(191,10,48,0.3)'}` }}>
                    <div className="card-header d-flex justify-content-between align-items-center"
                      style={{ background: 'rgba(191,10,48,0.15)', borderBottom: '1px solid rgba(191,10,48,0.3)', borderRadius: '12px 12px 0 0' }}>
                      <div>
                        <span className="fw-bold text-white" style={{ fontSize: '15px' }}>
                          <i className="fas fa-store me-2" style={{ color: '#bf0a30' }}></i>{pdv.pdv_name}
                        </span>
                        {pdv.city_name && <span className="text-light ms-2" style={{ fontSize: '12px' }}>{pdv.city_name}</span>}
                      </div>
                      {pdv.prizes.some(p => p.remaining_quantity === 0 && p.initial_quantity > 0) && (
                        <span className="badge me-1" style={{ background: '#ff4444', color: '#fff', fontSize: '11px' }}>
                          <i className="fas fa-times-circle me-1"></i>Épuisé
                        </span>
                      )}
                      {pdv.prizes.some(p => p.remaining_quantity <= 5 && p.remaining_quantity > 0) && (
                        <span className="badge" style={{ background: '#ff1a1a', color: '#fff', fontSize: '11px' }}>
                          <i className="fas fa-exclamation-triangle me-1"></i>Stock faible
                        </span>
                      )}
                    </div>
                    <div className="card-body p-0">
                      <div style={{ overflowX: 'auto' }}>
                        <table className="table table-dark mb-0" style={{ borderColor: 'rgba(191,10,48,0.2)' }}>
                          <thead>
                            <tr style={{ background: 'rgba(0,0,0,0.2)' }}>
                              <th className="text-light" style={{ fontSize: '12px', minWidth: '130px', paddingLeft: '16px', position: 'sticky', left: 0, zIndex: 2, background: 'rgba(20,10,10,0.98)' }}>Lot</th>
                              <th className="text-center text-light" style={{ fontSize: '12px', minWidth: '70px' }}>Initial</th>
                              {dailyData.days.map((day, i) => (
                                <th key={day} className="text-center text-light" style={{ fontSize: '11px', minWidth: '90px' }}>
                                  J{i + 1} · {new Date(day + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                                </th>
                              ))}
                              <th className="text-center text-white fw-bold" style={{ fontSize: '12px', minWidth: '80px' }}>Restant</th>
                              <th className="text-center text-light" style={{ fontSize: '12px', minWidth: '90px' }}>Réappro</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pdv.prizes.map(prize => {
                              const rem = prize.remaining_quantity;
                              const remAlert = rem <= 5 && rem > 0;
                              const remEmpty = rem === 0;
                              return (
                                <tr key={prize.prize_type} style={{ borderColor: 'rgba(191,10,48,0.1)' }}>
                                  <td style={{ paddingLeft: '16px', position: 'sticky', left: 0, zIndex: 1, background: 'rgba(62,39,35,0.98)' }}>
                                    <div className="d-flex align-items-center gap-2">
                                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: prize.color, display: 'inline-block', flexShrink: 0 }}></span>
                                      <span className="text-white" style={{ fontSize: '13px' }}>{prize.prize_label}</span>
                                    </div>
                                  </td>
                                  <td className="text-center text-light" style={{ fontSize: '13px' }}>{prize.initial_quantity}</td>
                                  {prize.days.map(cell => {
                                    const a = cell.stock_start <= 5 && cell.stock_start > 0;
                                    const e = cell.stock_start === 0;
                                    return (
                                      <td key={cell.date} className="text-center"
                                        style={{ background: e ? 'rgba(191,10,48,0.12)' : a ? 'rgba(255,26,26,0.08)' : 'transparent' }}>
                                        <span style={{ fontWeight: 'bold', fontSize: '13px', color: e ? '#ff6b6b' : a ? '#ff1a1a' : '#ccc' }}>
                                          {e && <i className="fas fa-times-circle me-1" style={{ fontSize: '10px' }}></i>}
                                          {a && !e && <i className="fas fa-exclamation-triangle me-1" style={{ fontSize: '10px' }}></i>}
                                          {cell.stock_start}
                                        </span>
                                        {cell.wins > 0 && <div style={{ fontSize: '9px', color: '#999' }}>−{cell.wins}</div>}
                                        {cell.restock > 0 && <div style={{ fontSize: '9px', color: '#6fcf6f' }}>+{cell.restock}</div>}
                                      </td>
                                    );
                                  })}
                                  <td className="text-center"
                                    style={{ background: remEmpty ? 'rgba(191,10,48,0.2)' : remAlert ? 'rgba(255,26,26,0.12)' : 'transparent' }}>
                                    <span style={{ fontWeight: 'bold', fontSize: '15px', color: remEmpty ? '#ff6b6b' : remAlert ? '#ff1a1a' : '#6fcf6f' }}>
                                      {remEmpty && <i className="fas fa-times-circle me-1"></i>}
                                      {remAlert && !remEmpty && <i className="fas fa-exclamation-triangle me-1"></i>}
                                      {rem}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    <button className="btn btn-sm"
                                      style={{ background: 'linear-gradient(135deg,#bf0a30,#8c0824)', color: '#fff', border: 'none', fontSize: '11px', padding: '3px 8px' }}
                                      onClick={() => {
                                        setRestockPrize({ ...prize, pdv_name: pdv.pdv_name });
                                        setRestockQty(0);
                                        setRestockDate(new Date().toISOString().split('T')[0]);
                                        setShowRestockModal(true);
                                      }}>
                                      <i className="fas fa-plus me-1"></i>Réappro
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                            {/* Ligne Réessayez */}
                            {(() => {
                              const tryAgainMap = pdv.try_again_by_day || {};
                              const totalTryAgain = Object.values(tryAgainMap).reduce((a, b) => a + b, 0);
                              return (
                                <tr style={{ borderTop: '1px dashed rgba(191,10,48,0.4)', background: 'rgba(191,10,48,0.06)' }}>
                                  <td style={{ paddingLeft: '16px', position: 'sticky', left: 0, zIndex: 1, background: 'rgba(80,20,20,0.98)' }}>
                                    <div className="d-flex align-items-center gap-2">
                                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ff6b6b', display: 'inline-block', flexShrink: 0 }}></span>
                                      <span style={{ fontSize: '13px', color: '#ff6b6b', fontWeight: 600 }}>Réessayez</span>
                                    </div>
                                  </td>
                                  <td className="text-center" style={{ fontSize: '13px', color: '#666' }}>—</td>
                                  {dailyData.days.map(day => {
                                    const count = tryAgainMap[day] ?? 0;
                                    return (
                                      <td key={day} className="text-center">
                                        <span style={{ fontWeight: count > 0 ? 'bold' : 'normal', fontSize: '13px', color: count > 0 ? '#ff6b6b' : '#555' }}>
                                          {count > 0 ? count : '—'}
                                        </span>
                                      </td>
                                    );
                                  })}
                                  <td className="text-center">
                                    <span style={{ fontWeight: 'bold', fontSize: '15px', color: totalTryAgain > 0 ? '#ff6b6b' : '#555' }}>
                                      {totalTryAgain > 0 ? totalTryAgain : '—'}
                                    </span>
                                  </td>
                                  <td></td>
                                </tr>
                              );
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })}
              <p className="text-light small mt-1">
                <i className="fas fa-exclamation-triangle me-1" style={{ color: '#ff1a1a' }}></i>Alerte &lt; 5 —
                <i className="fas fa-times-circle ms-2 me-1" style={{ color: '#ff6b6b' }}></i>Épuisé
              </p>
            </div>
          )}
        </div>
      )}

      </div>{/* fin container */}

      {/* Modal Réapprovisionnement */}
      {showRestockModal && restockPrize && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: '#3E2723', border: '2px solid #bf0a30' }}>
              <div className="modal-header border-bottom-0">
                <h5 className="modal-title text-white"><i className="fas fa-plus-circle me-2" style={{ color: '#bf0a30' }}></i>Réapprovisionnement</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowRestockModal(false)}></button>
              </div>
              <div className="modal-body">
                <p className="text-light mb-1">PDV : <strong className="text-white">{restockPrize.pdv_name}</strong></p>
                <p className="text-light mb-3">Lot : <strong className="text-white">{restockPrize.prize_label}</strong></p>
                <div className="mb-3">
                  <label className="form-label text-light small">Date du réapprovisionnement</label>
                  <input
                    type="date"
                    className="form-control"
                    value={restockDate}
                    onChange={e => setRestockDate(e.target.value)}
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(191,10,48,0.4)', color: '#fff' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label text-light small">Quantité à ajouter</label>
                  <div className="input-group">
                    <button className="btn" style={{ background: 'rgba(191,10,48,0.3)', borderColor: 'rgba(191,10,48,0.4)', color: '#fff' }}
                      onClick={() => setRestockQty(q => Math.max(0, q - 1))}>
                      <i className="fas fa-minus"></i>
                    </button>
                    <input
                      type="number" min="0"
                      className="form-control text-center fw-bold"
                      value={restockQty}
                      onChange={e => setRestockQty(Math.max(0, parseInt(e.target.value) || 0))}
                      style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(191,10,48,0.4)', color: '#fff', fontSize: '20px' }}
                    />
                    <button className="btn" style={{ background: 'rgba(191,10,48,0.3)', borderColor: 'rgba(191,10,48,0.4)', color: '#fff' }}
                      onClick={() => setRestockQty(q => q + 1)}>
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                </div>
                <div className="p-2 rounded" style={{ background: 'rgba(191,10,48,0.1)', border: '1px solid rgba(191,10,48,0.3)' }}>
                  <small className="text-light">
                    Stock actuel : <strong className="text-white">{restockPrize.remaining_quantity}</strong> →
                    Après réappro : <strong className="text-white">{restockPrize.remaining_quantity + restockQty}</strong>
                  </small>
                </div>
              </div>
              <div className="modal-footer border-top-0">
                <button className="btn btn-outline-light btn-sm" onClick={() => setShowRestockModal(false)}>Annuler</button>
                <button
                  className="btn btn-sm"
                  style={{ background: 'linear-gradient(135deg, #bf0a30, #8c0824)', color: '#fff', border: 'none', fontWeight: 'bold' }}
                  disabled={restockQty <= 0}
                  onClick={handleRestock}
                >
                  <i className="fas fa-check me-2"></i>Confirmer +{restockQty}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Stock Modal */}
      {showEditModal && selectedSession && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: '#3E2723', border: '2px solid #bf0a30' }}>
              <div className="modal-header border-bottom-0">
                <h5 className="modal-title text-white">
                  <i className="fas fa-edit me-2" style={{ color: '#bf0a30' }}></i>
                  Modifier le stock journalier
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label text-light">Point de Vente</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedSession.pdv_name}
                    disabled
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#fff' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label text-light">Lot</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editStock.prizeLabel || prizes.find(p => p.type === editStock.prizeId)?.label || editStock.prizeId}
                    disabled
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#fff' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label text-light">Stock maximum (quota)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={editStock.maxQuantity}
                    onChange={(e) => setEditStock({ ...editStock, maxQuantity: parseInt(e.target.value) || 0 })}
                    min="0"
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#fff' }}
                  />
                  <small className="text-muted">Nombre maximum de lots disponibles pour la journée</small>
                </div>
                <div className="mb-3">
                  <label className="form-label text-light">Quantité utilisée</label>
                  <input
                    type="number"
                    className="form-control"
                    value={editStock.usedQuantity}
                    onChange={(e) => setEditStock({ ...editStock, usedQuantity: parseInt(e.target.value) || 0 })}
                    min="0"
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#fff' }}
                  />
                  <small className="text-muted">Nombre de lots déjà distribués aujourd'hui</small>
                </div>
                <div className="alert alert-info" style={{ background: 'rgba(13, 110, 253, 0.1)', borderColor: 'rgba(13, 110, 253, 0.3)' }}>
                  <small className="text-light">
                    <i className="fas fa-info-circle me-2"></i>
                    Stock restant: <strong>{Math.max(0, editStock.maxQuantity - editStock.usedQuantity)}</strong>
                  </small>
                </div>
              </div>
              <div className="modal-footer border-top-0">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Annuler</button>
                <button type="button" className="btn btn-danger" onClick={handleSaveStock}>
                  <i className="fas fa-save me-2"></i>Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gain Mode Modal */}
      {showGainModeModal && selectedSessionForGainMode && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: '#3E2723', border: '2px solid #bf0a30' }}>
              <div className="modal-header border-bottom-0">
                <h5 className="modal-title text-white">
                  <i className="fas fa-cog me-2" style={{ color: '#bf0a30' }}></i>
                  Changer le mode de gain
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowGainModeModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label text-light">Point de Vente</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedSessionForGainMode.pdv_name}
                    disabled
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#fff' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label text-light">Mode de gain actuel</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedSessionForGainMode.gain_mode === 'FORCED' ? 'Gagnant' : selectedSessionForGainMode.gain_mode === 'RANDOM' ? 'Aléatoire' : 'Probabilité'}
                    disabled
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#fff' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label text-light fw-bold">Nouveau mode de gain</label>
                  <select
                    className="form-select"
                    value={newGainMode}
                    onChange={(e) => setNewGainMode(e.target.value)}
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#fff' }}
                  >
                    <option value="PROBABILITY">Probabilité (ratio configuré)</option>
                    <option value="FORCED">Gagnant (100% de gain)</option>
                    <option value="RANDOM">Aléatoire (tirage au sort)</option>
                  </select>
                  <div className="mt-2">
                    {newGainMode === 'PROBABILITY' && (
                      <small className="text-info">
                        <i className="fas fa-info-circle me-1"></i>
                        Chaque lot a sa propre probabilité de gain selon le ratio configuré
                      </small>
                    )}
                    {newGainMode === 'FORCED' && (
                      <small className="text-warning">
                        <i className="fas fa-exclamation-triangle me-1"></i>
                        <strong>Tous les joueurs gagnent !</strong> Utilisez ce mode pour les promotions spéciales
                      </small>
                    )}
                    {newGainMode === 'RANDOM' && (
                      <small className="text-muted">
                        <i className="fas fa-random me-1"></i>
                        Tirage aléatoire parmi tous les lots disponibles (probabilité égale)
                      </small>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer border-top-0">
                <button type="button" className="btn btn-secondary" onClick={() => setShowGainModeModal(false)}>Annuler</button>
                <button type="button" className="btn btn-danger" onClick={handleSaveGainMode}>
                  <i className="fas fa-save me-2"></i>Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Stock Modal */}
      {showAddStockModal && selectedSessionForAddStock && selectedPrizeForAddStock && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: '#3E2723', border: '2px solid #bf0a30' }}>
              <div className="modal-header border-bottom-0">
                <h5 className="modal-title text-white">
                  <i className="fas fa-plus me-2" style={{ color: '#bf0a30' }}></i>
                  Ajouter du stock
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowAddStockModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label text-light">Point de Vente</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedSessionForAddStock.pdv_name}
                    disabled
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#fff' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label text-light">Lot</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedPrizeForAddStock.prize_label}
                    disabled
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#fff' }}
                  />
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label text-light">Stock actuel</label>
                    <input
                      type="text"
                      className="form-control"
                      value={selectedPrizeForAddStock.max_quantity}
                      disabled
                      style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#fff' }}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label text-light">Utilisé</label>
                    <input
                      type="text"
                      className="form-control"
                      value={selectedPrizeForAddStock.used_quantity}
                      disabled
                      style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#fff' }}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label text-light fw-bold">Quantité à ajouter</label>
                  <input
                    type="number"
                    className="form-control"
                    value={stockToAdd}
                    onChange={(e) => setStockToAdd(parseInt(e.target.value) || 0)}
                    min="1"
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#fff', fontSize: '18px', fontWeight: 'bold' }}
                  />
                  <small className="text-muted">Nombre de lots à ajouter au stock actuel</small>
                </div>
                <div className="alert alert-success" style={{ background: 'rgba(25, 135, 84, 0.2)', borderColor: 'rgba(25, 135, 84, 0.4)' }}>
                  <small className="text-light">
                    <i className="fas fa-info-circle me-2"></i>
                    Nouveau stock maximum : <strong>{selectedPrizeForAddStock.max_quantity + stockToAdd}</strong>
                    <br />
                    Stock restant après ajout : <strong>{Math.max(0, selectedPrizeForAddStock.max_quantity + stockToAdd - selectedPrizeForAddStock.used_quantity)}</strong>
                  </small>
                </div>
              </div>
              <div className="modal-footer border-top-0">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddStockModal(false)}>Annuler</button>
                <button type="button" className="btn btn-success" onClick={handleSaveAddStock} disabled={stockToAdd <= 0}>
                  <i className="fas fa-plus me-2"></i>Ajouter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stocks;
